import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const SocketContext = createContext();
const socketDebugEnabled = process.env.REACT_APP_DEBUG_SOCKET === 'true';
const debugLog = (...args) => {
    if (socketDebugEnabled) {
    }
};

export { SocketContext };

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
};

// Hook specifically for community chat
export const useCommunityChat = () => {
    const { communityChat } = useSocket();
    return communityChat;
};

// Hook specifically for unread count
export const useUnreadCount = () => {
    const { unreadCount } = useSocket();
    return unreadCount;
};

// Hook specifically for unread request count
export const useUnreadRequestCount = () => {
    const { unreadRequestCount } = useSocket();
    return unreadRequestCount;
};

// Hook specifically for livestream list
export const useLivestreamList = () => {
    const { livestreamList } = useSocket();
    return livestreamList;
};

// Hook specifically for notification unread count
export const useNotificationUnreadCount = () => {
    const { notificationUnreadCount } = useSocket();
    return notificationUnreadCount;
};

// Hook for account switching and connection management
export const useSocketAccountSwitch = () => {
    const { currentToken, refreshConnections, disconnectAll } = useSocket();

    return {
        currentToken,
        refreshConnections,
        disconnectAll,
        // Helper function to call after account switch
        handleAccountSwitch: () => {
            debugLog('🔄 Account switch triggered manually');
            refreshConnections();
        }
    };
};

export const SocketProvider = ({ children }) => {
    // Token tracking for account switches
    const auth = useSelector((state) => state.auth);
    const authRef = useRef(auth);
    authRef.current = auth;
    // console.log('authRef.current:', auth, authRef.current.auth);
    const [currentToken, setCurrentToken] = useState(auth.accessToken || '');
    const currentTokenRef = useRef(currentToken);
    // Community Chat WebSocket state
    const [communityChatSocket, setCommunityChatSocket] = useState(null);
    const [communityChatConnected, setCommunityChatConnected] = useState(false);
    const [communityChatStatus, setCommunityChatStatus] = useState('Disconnected');
    const [communityChatMessages, setCommunityChatMessages] = useState([]);
    const [communityChatHasMore, setCommunityChatHasMore] = useState(false);
    const [communityChatNextCursor, setCommunityChatNextCursor] = useState(null);
    const [communityChatNextBucket, setCommunityChatNextBucket] = useState(null);

    // Chat Room List WebSocket state
    const [chatRoomListSocket, setChatRoomListSocket] = useState(null);
    const [chatRoomListConnected, setChatRoomListConnected] = useState(false);
    const [chatRoomListStatus, setChatRoomListStatus] = useState('Disconnected');
    const [chatRoomList, setChatRoomList] = useState([]);
    const [chatRoomListNextCursor, setChatRoomListNextCursor] = useState(null);
    const [chatRoomListHasMore, setChatRoomListHasMore] = useState(false);
    const [chatRoomListIsFetchingMore, setChatRoomListIsFetchingMore] = useState(false);
    const chatRoomListSocketRef = useRef(null);
    const chatRoomListIsFetchMoreRef = useRef(false);

    // Message Request List WebSocket state
    const [messageRequestListSocket, setMessageRequestListSocket] = useState(null);
    const [messageRequestListConnected, setMessageRequestListConnected] = useState(false);
    const [messageRequestListStatus, setMessageRequestListStatus] = useState('Disconnected');
    const [messageRequestList, setMessageRequestList] = useState([]);

    // Unread Count WebSocket state
    const [unreadCountSocket, setUnreadCountSocket] = useState(null);
    const [unreadCountConnected, setUnreadCountConnected] = useState(false);
    const [unreadCountStatus, setUnreadCountStatus] = useState('Disconnected');
    const [totalUnreadCount, setTotalUnreadCount] = useState({ total_unread_messages: 0 });

    // Unread Request Count WebSocket state
    const [unreadRequestCountSocket, setUnreadRequestCountSocket] = useState(null);
    const [unreadRequestCountConnected, setUnreadRequestCountConnected] = useState(false);
    const [unreadRequestCountStatus, setUnreadRequestCountStatus] = useState('Disconnected');
    const [totalUnreadRequestCount, setTotalUnreadRequestCount] = useState({ total_unread_messages: 0 });

    // Livestream List WebSocket state
    const [livestreamListSocket, setLivestreamListSocket] = useState(null);
    const [livestreamListConnected, setLivestreamListConnected] = useState(false);
    const [livestreamListStatus, setLivestreamListStatus] = useState('Disconnected');
    const [livestreamList, setLivestreamList] = useState([]);

    // Notification Unread Count WebSocket state
    const [notificationUnreadCountSocket, setNotificationUnreadCountSocket] = useState(null);
    const [notificationUnreadCountConnected, setNotificationUnreadCountConnected] = useState(false);
    const [notificationUnreadCountStatus, setNotificationUnreadCountStatus] = useState('Disconnected');
    const [notificationUnreadCount, setNotificationUnreadCount] = useState({ unread_count: 0 });

    // Refs for reconnection management
    const communityChatReconnectTimeoutRef = useRef(null);
    const communityChatReconnectAttemptsRef = useRef(0);
    const communityChatConnectingRef = useRef(false);
    const communityChatIntentionalCloseRef = useRef(false);
    const communityChatSocketRef = useRef(null); // tracks actual socket instance (avoids stale-closure duplicate)
    const chatRoomListReconnectTimeoutRef = useRef(null);
    const chatRoomListReconnectAttemptsRef = useRef(0);
    const chatRoomListConnectingRef = useRef(false);
    const chatRoomListIntentionalCloseRef = useRef(false);
    const messageRequestListReconnectTimeoutRef = useRef(null);
    const messageRequestListReconnectAttemptsRef = useRef(0);
    const messageRequestListConnectingRef = useRef(false);
    const messageRequestListIntentionalCloseRef = useRef(false);
    const unreadCountReconnectTimeoutRef = useRef(null);
    const unreadCountReconnectAttemptsRef = useRef(0);
    const unreadCountConnectingRef = useRef(false);
    const unreadCountIntentionalCloseRef = useRef(false);
    const unreadRequestCountReconnectTimeoutRef = useRef(null);
    const unreadRequestCountReconnectAttemptsRef = useRef(0);
    const unreadRequestCountConnectingRef = useRef(false);
    const unreadRequestCountIntentionalCloseRef = useRef(false);
    const livestreamListReconnectTimeoutRef = useRef(null);
    const livestreamListReconnectAttemptsRef = useRef(0);
    const livestreamListConnectingRef = useRef(false);
    const livestreamListIntentionalCloseRef = useRef(false);
    const notificationUnreadCountReconnectTimeoutRef = useRef(null);
    const notificationUnreadCountReconnectAttemptsRef = useRef(0);
    const notificationUnreadCountConnectingRef = useRef(false);
    const notificationUnreadCountIntentionalCloseRef = useRef(false);
    const maxReconnectAttempts = 2;
    const minStableConnectionTime = 10000; // 10 seconds - connection must be stable this long to reset attempts

    // Track connection times to determine if connection is stable
    const communityChatConnectTimeRef = useRef(null);
    const chatRoomListConnectTimeRef = useRef(null);
    const messageRequestListConnectTimeRef = useRef(null);
    const unreadCountConnectTimeRef = useRef(null);
    const unreadRequestCountConnectTimeRef = useRef(null);
    const livestreamListConnectTimeRef = useRef(null);
    const notificationUnreadCountConnectTimeRef = useRef(null);

    // Monitor token changes for account switching
    useEffect(() => {
        const newToken = auth.accessToken || '';
        const oldToken = currentTokenRef.current;

        if (newToken === oldToken) return;

        debugLog('🔑 Token change detected! Old:', oldToken?.substring(0, 10) + '...', 'New:', newToken?.substring(0, 10) + '...');
        setCurrentToken(newToken);
        currentTokenRef.current = newToken;

        if (oldToken && newToken) {
            debugLog('👤 Account switch detected - reconnecting WebSockets...');
            disconnectAllWebSockets();
            reconnectAllWebSockets();
        } else if (oldToken && !newToken) {
            debugLog('🚪 Logout detected - disconnecting WebSockets...');
            disconnectAllWebSockets();
        } else if (!oldToken && newToken) {
            debugLog('🔑 Login detected - connecting WebSockets...');
            reconnectAllWebSockets();
        }
    }, [auth.accessToken]); // runs whenever the token actually changes

    const getAuthToken = () => {
        return authRef.current.accessToken || '';
    };

    // Disconnect all WebSocket connections
    const disconnectAllWebSockets = () => {
        debugLog('🔌 Disconnecting all WebSocket connections for account switch...');

        // Clear reconnect timeouts and reset flags
        if (communityChatReconnectTimeoutRef.current) {
            clearTimeout(communityChatReconnectTimeoutRef.current);
            communityChatReconnectTimeoutRef.current = null;
        }
        communityChatConnectingRef.current = false;
        communityChatReconnectAttemptsRef.current = 0;

        // Disconnect Community Chat
        if (communityChatSocket) {
            if (communityChatSocket.readyState !== WebSocket.CLOSED) {
                communityChatIntentionalCloseRef.current = true;
                communityChatSocket.close(1000, 'Account switch');
            }
            setCommunityChatSocket(null);
            setCommunityChatConnected(false);
            setCommunityChatStatus('Disconnected');
            setCommunityChatMessages([]);
        }

        // Disconnect Chat Room List
        if (chatRoomListSocket) {
            if (chatRoomListSocket.readyState !== WebSocket.CLOSED) {
                chatRoomListIntentionalCloseRef.current = true;
                chatRoomListSocket.close(1000, 'Account switch');
            }
            setChatRoomListSocket(null);
            chatRoomListSocketRef.current = null;
            setChatRoomListConnected(false);
            setChatRoomListStatus('Disconnected');
            setChatRoomList([]);
            setChatRoomListNextCursor(null);
            setChatRoomListHasMore(false);
            setChatRoomListIsFetchingMore(false);
            chatRoomListIsFetchMoreRef.current = false;
        }

        // Disconnect Message Request List
        if (messageRequestListSocket) {
            if (messageRequestListSocket.readyState !== WebSocket.CLOSED) {
                messageRequestListIntentionalCloseRef.current = true;
                messageRequestListSocket.close(1000, 'Account switch');
            }
            setMessageRequestListSocket(null);
            setMessageRequestListConnected(false);
            setMessageRequestListStatus('Disconnected');
            setMessageRequestList([]);
        }

        // Disconnect Unread Count
        if (unreadCountSocket) {
            if (unreadCountSocket.readyState !== WebSocket.CLOSED) {
                unreadCountIntentionalCloseRef.current = true;
                unreadCountSocket.close(1000, 'Account switch');
            }
            setUnreadCountSocket(null);
            setUnreadCountConnected(false);
            setUnreadCountStatus('Disconnected');
            setTotalUnreadCount({ total_unread_messages: 0 });
        }

        // Disconnect Unread Request Count
        if (unreadRequestCountSocket) {
            if (unreadRequestCountSocket.readyState !== WebSocket.CLOSED) {
                unreadRequestCountIntentionalCloseRef.current = true;
                unreadRequestCountSocket.close(1000, 'Account switch');
            }
            setUnreadRequestCountSocket(null);
            setUnreadRequestCountConnected(false);
            setUnreadRequestCountStatus('Disconnected');
            setTotalUnreadRequestCount({ total_unread_messages: 0 });
        }

        // Disconnect Livestream List
        if (livestreamListSocket) {
            if (livestreamListSocket.readyState !== WebSocket.CLOSED) {
                livestreamListIntentionalCloseRef.current = true;
                livestreamListSocket.close(1000, 'Account switch');
            }
            setLivestreamListSocket(null);
            setLivestreamListConnected(false);
            setLivestreamListStatus('Disconnected');
            setLivestreamList([]);
        }

        // Disconnect Notification Unread Count
        if (notificationUnreadCountSocket) {
            if (notificationUnreadCountSocket.readyState !== WebSocket.CLOSED) {
                notificationUnreadCountIntentionalCloseRef.current = true;
                notificationUnreadCountSocket.close(1000, 'Account switch');
            }
            setNotificationUnreadCountSocket(null);
            setNotificationUnreadCountConnected(false);
            setNotificationUnreadCountStatus('Disconnected');
            setNotificationUnreadCount({ unread_count: 0 });
        }
    };

    // Reconnect all WebSocket connections with new token
    const reconnectAllWebSockets = () => {
        debugLog('🔄 Reconnecting all WebSocket connections with new token...');

        // Reset all connecting flags and reconnect attempts
        communityChatConnectingRef.current = false;
        chatRoomListConnectingRef.current = false;
        messageRequestListConnectingRef.current = false;
        unreadCountConnectingRef.current = false;
        unreadRequestCountConnectingRef.current = false;
        livestreamListConnectingRef.current = false;
        notificationUnreadCountConnectingRef.current = false;

        communityChatReconnectAttemptsRef.current = 0;
        chatRoomListReconnectAttemptsRef.current = 0;
        messageRequestListReconnectAttemptsRef.current = 0;
        unreadCountReconnectAttemptsRef.current = 0;
        unreadRequestCountReconnectAttemptsRef.current = 0;
        livestreamListReconnectAttemptsRef.current = 0;
        notificationUnreadCountReconnectAttemptsRef.current = 0;

        // Small delay to ensure clean disconnection
        setTimeout(() => {
            connectCommunityChatWebSocket();
            connectChatRoomListWebSocket();
            connectMessageRequestListWebSocket();
            connectUnreadCountWebSocket();
            connectUnreadRequestCountWebSocket();
            connectLivestreamListWebSocket();
            connectNotificationUnreadCountWebSocket();
        }, 500);
    };

    // Community Chat WebSocket Connection
    const connectCommunityChatWebSocket = () => {
        try {
            // Prevent duplicate connection attempts
            if (communityChatConnectingRef.current) {
                debugLog('⚠️ Community Chat connection already in progress, skipping');
                return null;
            }

            // Close existing socket if any to prevent duplicates — use ref (not state) to avoid stale closure
            const existingSocket = communityChatSocketRef.current;
            if (existingSocket && existingSocket.readyState !== WebSocket.CLOSED) {
                debugLog('🔌 Closing existing Community Chat socket before creating new one');
                communityChatIntentionalCloseRef.current = true;
                existingSocket.close(1000, 'Reconnecting');
                communityChatSocketRef.current = null;
                setCommunityChatSocket(null);
            }

            const token = getAuthToken();
            if (!token) {
                debugLog('⚠️ No token available, cannot connect Community Chat WebSocket');
                setCommunityChatStatus('No Token');
                return null;
            }

            communityChatConnectingRef.current = true;

            const wsUrl = `${process.env.REACT_APP_SOCKET_URL}/community-chat/?token=${token}`;

            setCommunityChatStatus('Connecting...');
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                debugLog('Community Chat WebSocket connected successfully');
                communityChatConnectingRef.current = false;
                communityChatConnectTimeRef.current = Date.now();
                communityChatSocketRef.current = ws;
                setCommunityChatConnected(true);
                setCommunityChatStatus('Connected');
                setCommunityChatSocket(ws);
                // History is requested by Chat.jsx after it registers its listener,
                // avoiding the race where previous_messages arrives before addEventListener is set up.
            };

            ws.onclose = (event) => {
                debugLog('Community Chat WebSocket disconnected:', event.code, event.reason);
                communityChatConnectingRef.current = false;
                if (communityChatSocketRef.current === ws) communityChatSocketRef.current = null;
                setCommunityChatConnected(false);
                setCommunityChatSocket(null);

                // Check if connection was stable (lasted more than minStableConnectionTime)
                const connectionDuration = communityChatConnectTimeRef.current
                    ? Date.now() - communityChatConnectTimeRef.current
                    : 0;

                if (connectionDuration >= minStableConnectionTime) {
                    debugLog('✅ Connection was stable, resetting reconnect attempts');
                    communityChatReconnectAttemptsRef.current = 0;
                }
                communityChatConnectTimeRef.current = null;

                // Clear any pending reconnect timeout
                if (communityChatReconnectTimeoutRef.current) {
                    clearTimeout(communityChatReconnectTimeoutRef.current);
                    communityChatReconnectTimeoutRef.current = null;
                }

                // Don't reconnect if it was an intentional close
                if (communityChatIntentionalCloseRef.current) {
                    debugLog('⚠️ Intentional close, skipping reconnect');
                    communityChatIntentionalCloseRef.current = false;
                    setCommunityChatStatus('Disconnected');
                    return;
                }

                // Check if max attempts reached
                if (communityChatReconnectAttemptsRef.current >= maxReconnectAttempts) {
                    debugLog('❌ Max reconnect attempts reached, stopping reconnection');
                    setCommunityChatStatus('Connection Failed - Max Retries Reached');
                    return;
                }

                if (event.code === 1000 || event.code === 1001) {
                    setCommunityChatStatus('Disconnected');
                } else {
                    setCommunityChatStatus('Connection Lost');
                    handleCommunityChatReconnect();
                }
            };

            ws.onerror = (error) => {
                console.error('Community Chat WebSocket error:', error);
                communityChatConnectingRef.current = false;
                // Don't change status here, let onclose handle it
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    // Check if it's previous messages (initial chat history or load more)
                    if (data.type === 'previous_messages' && Array.isArray(data.messages)) {
                        debugLog('📥 Loaded chat history:', data.messages.length, 'messages');
                        setCommunityChatMessages(data.messages);
                        setCommunityChatHasMore(data.has_more || false);
                        setCommunityChatNextCursor(data.next_cursor || null);
                        setCommunityChatNextBucket(data.next_bucket || null);
                    } else if (data.type === 'chat_message' || (!data.type && data.message)) {
                        // Handle single message object (real-time message)
                        debugLog('💬 Received real-time message:', data.message);
                        // Deduplicate messages based on ID and timestamp
                        setCommunityChatMessages(prev => {
                            const messageId = data.id || data.created_at;
                            const isDuplicate = prev.some(msg =>
                                (msg.id === messageId) ||
                                (msg.created_at === data.created_at && msg.message === data.message)
                            );
                            if (isDuplicate) {
                                debugLog('⚠️ Duplicate message detected, skipping:', data.message);
                                return prev;
                            }
                            return [...prev, data];
                        });
                    } else if (Array.isArray(data)) {
                        // Handle array of messages (fallback for other formats)
                        debugLog('📥 Received message array:', data.length, 'messages');
                        setCommunityChatMessages(data);
                    } else {
                        // Handle other message types
                        debugLog('📨 Received other message type:', data.type || 'unknown');
                        if (data.message) {
                            // Deduplicate for other message types too
                            setCommunityChatMessages(prev => {
                                const messageId = data.id || data.created_at;
                                const isDuplicate = prev.some(msg =>
                                    (msg.id === messageId) ||
                                    (msg.created_at === data.created_at && msg.message === data.message)
                                );
                                if (isDuplicate) {
                                    debugLog('⚠️ Duplicate message detected, skipping:', data.message);
                                    return prev;
                                }
                                return [...prev, data];
                            });
                        }
                    }
                } catch (e) {
                    console.error('❌ Error parsing Community Chat WebSocket message:', e);
                }
            };

            return ws;
        } catch (error) {
            console.error('❌ Failed to connect Community Chat WebSocket:', error);
            communityChatConnectingRef.current = false;
            setCommunityChatStatus('Connection Failed');
            return null;
        }
    };

    // Chat Room List WebSocket Connection
    const connectChatRoomListWebSocket = () => {
        try {
            // Prevent duplicate connection attempts
            if (chatRoomListConnectingRef.current) {
                debugLog('⚠️ Chat Room List connection already in progress, skipping');
                return null;
            }

            // Close existing socket if any to prevent duplicates
            if (chatRoomListSocket && chatRoomListSocket.readyState !== WebSocket.CLOSED) {
                debugLog('🔌 Closing existing Chat Room List socket before creating new one');
                chatRoomListIntentionalCloseRef.current = true;
                chatRoomListSocket.close(1000, 'Reconnecting');
                setChatRoomListSocket(null);
            }

            const token = getAuthToken();
            if (!token) {
                debugLog('⚠️ No token available, cannot connect Chat Room List WebSocket');
                setChatRoomListStatus('No Token');
                return null;
            }

            chatRoomListConnectingRef.current = true;
            // const wsUrl = `REACT_APP_SOCKET_URL/chat-room-list/?token=${token}`;
            const wsUrl = `${process.env.REACT_APP_SOCKET_URL}/chat-room-list/?token=${token}`;

            setChatRoomListStatus('Connecting...');
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                chatRoomListConnectingRef.current = false;
                chatRoomListConnectTimeRef.current = Date.now();
                setChatRoomListConnected(true);
                setChatRoomListStatus('Connected');
                setChatRoomListSocket(ws);
                chatRoomListSocketRef.current = ws;
            };

            ws.onclose = (event) => {
                chatRoomListConnectingRef.current = false;
                setChatRoomListConnected(false);
                setChatRoomListSocket(null);

                // Check if connection was stable
                const connectionDuration = chatRoomListConnectTimeRef.current
                    ? Date.now() - chatRoomListConnectTimeRef.current
                    : 0;

                if (connectionDuration >= minStableConnectionTime) {
                    debugLog('✅ Chat Room List connection was stable, resetting reconnect attempts');
                    chatRoomListReconnectAttemptsRef.current = 0;
                }
                chatRoomListConnectTimeRef.current = null;

                // Don't reconnect if it was an intentional close
                if (chatRoomListIntentionalCloseRef.current) {
                    debugLog('⚠️ Intentional close, skipping reconnect');
                    chatRoomListIntentionalCloseRef.current = false;
                    setChatRoomListStatus('Disconnected');
                    return;
                }

                // Check if max attempts reached
                if (chatRoomListReconnectAttemptsRef.current >= maxReconnectAttempts) {
                    debugLog('❌ Chat Room List: Max reconnect attempts reached');
                    setChatRoomListStatus('Connection Failed - Max Retries Reached');
                    return;
                }

                if (event.code === 1000) {
                    setChatRoomListStatus('Disconnected');
                } else {
                    setChatRoomListStatus('Connection Lost');
                    handleChatRoomListReconnect();
                }
            };

            ws.onerror = (error) => {
                console.error('Chat Room List WebSocket error:', error);
                chatRoomListConnectingRef.current = false;
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    const isFetchMore = chatRoomListIsFetchMoreRef.current;

                    if (Array.isArray(data)) {
                        setChatRoomList(data);
                        setChatRoomListNextCursor(null);
                        setChatRoomListHasMore(false);
                        chatRoomListIsFetchMoreRef.current = false;
                        setChatRoomListIsFetchingMore(false);
                    } else if (data && Array.isArray(data.chat_room_detail_list)) {
                        if (isFetchMore) {
                            setChatRoomList(prev => [...prev, ...data.chat_room_detail_list]);
                        } else {
                            setChatRoomList(data.chat_room_detail_list);
                        }
                        const nextCursor = data.next_cursor || data.next || null;
                        setChatRoomListNextCursor(nextCursor);
                        setChatRoomListHasMore(!!nextCursor);
                        chatRoomListIsFetchMoreRef.current = false;
                        setChatRoomListIsFetchingMore(false);
                    }
                } catch (e) {
                    // ignore parse errors
                }
            };
            return ws;
        } catch (error) {
            chatRoomListConnectingRef.current = false;
            setChatRoomListStatus('Connection Failed');
            return null;
        }
    };

    // Message Request List WebSocket Connection
    const connectMessageRequestListWebSocket = () => {
        try {
            // Prevent duplicate connection attempts
            if (messageRequestListConnectingRef.current) {
                debugLog('⚠️ Message Request List connection already in progress, skipping');
                return null;
            }

            // Close existing socket if any to prevent duplicates
            if (messageRequestListSocket && messageRequestListSocket.readyState !== WebSocket.CLOSED) {
                debugLog('🔌 Closing existing Message Request List socket before creating new one');
                messageRequestListIntentionalCloseRef.current = true;
                messageRequestListSocket.close(1000, 'Reconnecting');
                setMessageRequestListSocket(null);
            }

            const token = getAuthToken();
            if (!token) {
                debugLog('⚠️ No token available, cannot connect Message Request List WebSocket');
                setMessageRequestListStatus('No Token');
                return null;
            }

            messageRequestListConnectingRef.current = true;
            const wsUrl = `${process.env.REACT_APP_SOCKET_URL}/message-request-list/?token=${token}`;

            debugLog('Connecting to Message Request List WebSocket:', wsUrl);
            setMessageRequestListStatus('Connecting...');
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                debugLog('Message Request List WebSocket connected successfully');
                messageRequestListConnectingRef.current = false;
                messageRequestListConnectTimeRef.current = Date.now();
                setMessageRequestListConnected(true);
                setMessageRequestListStatus('Connected');
                setMessageRequestListSocket(ws);
            };

            ws.onclose = (event) => {
                debugLog('Message Request List WebSocket disconnected:', event.code, event.reason);
                messageRequestListConnectingRef.current = false;
                setMessageRequestListConnected(false);
                setMessageRequestListSocket(null);

                // Check if connection was stable
                const connectionDuration = messageRequestListConnectTimeRef.current
                    ? Date.now() - messageRequestListConnectTimeRef.current
                    : 0;

                if (connectionDuration >= minStableConnectionTime) {
                    debugLog('✅ Message Request List connection was stable, resetting reconnect attempts');
                    messageRequestListReconnectAttemptsRef.current = 0;
                }
                messageRequestListConnectTimeRef.current = null;

                // Don't reconnect if it was an intentional close
                if (messageRequestListIntentionalCloseRef.current) {
                    debugLog('⚠️ Intentional close, skipping reconnect');
                    messageRequestListIntentionalCloseRef.current = false;
                    setMessageRequestListStatus('Disconnected');
                    return;
                }

                // Check if max attempts reached
                if (messageRequestListReconnectAttemptsRef.current >= maxReconnectAttempts) {
                    debugLog('❌ Message Request List: Max reconnect attempts reached');
                    setMessageRequestListStatus('Connection Failed - Max Retries Reached');
                    return;
                }

                if (event.code === 1000) {
                    setMessageRequestListStatus('Disconnected');
                } else {
                    setMessageRequestListStatus('Connection Lost');
                    handleMessageRequestListReconnect();
                }
            };

            ws.onerror = (error) => {
                console.error('Message Request List WebSocket error:', error);
                messageRequestListConnectingRef.current = false;
                setMessageRequestListStatus('Connection Error');
            };

            ws.onmessage = (event) => {
                try {
                    debugLog('Message Request List WebSocket message received:', event.data);
                    const data = JSON.parse(event.data);
                    // Accepts either array or {message_request_list: []}
                    if (Array.isArray(data)) {
                        setMessageRequestList(data);
                    } else if (data && Array.isArray(data.message_request_list)) {
                        setMessageRequestList(data.message_request_list);
                    }
                } catch (e) {
                    console.error('Error parsing Message Request List WebSocket message:', e);
                }
            };

            return ws;
        } catch (error) {
            messageRequestListConnectingRef.current = false;
            setMessageRequestListStatus('Connection Failed');
            return null;
        }
    };

    // Unread Count WebSocket Connection
    const connectUnreadCountWebSocket = () => {
        try {
            // Prevent duplicate connection attempts
            if (unreadCountConnectingRef.current) {
                debugLog('⚠️ Unread Count connection already in progress, skipping');
                return null;
            }

            // Close existing socket if any to prevent duplicates
            if (unreadCountSocket && unreadCountSocket.readyState !== WebSocket.CLOSED) {
                debugLog('🔌 Closing existing Unread Count socket before creating new one');
                unreadCountIntentionalCloseRef.current = true;
                unreadCountSocket.close(1000, 'Reconnecting');
                setUnreadCountSocket(null);
            }

            const token = getAuthToken();
            if (!token) {
                debugLog('⚠️ No token available, cannot connect Unread Count WebSocket');
                setUnreadCountStatus('No Token');
                return null;
            }

            unreadCountConnectingRef.current = true;
            const wsUrl = `${process.env.REACT_APP_SOCKET_URL}/chat/unread-count/?token=${token}`;


            debugLog('Connecting to Unread Count WebSocket:', wsUrl);
            setUnreadCountStatus('Connecting...');
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                debugLog('Unread Count WebSocket connected successfully');
                unreadCountConnectingRef.current = false;
                unreadCountConnectTimeRef.current = Date.now();
                setUnreadCountConnected(true);
                setUnreadCountStatus('Connected');
                setUnreadCountSocket(ws);
            };

            ws.onclose = (event) => {
                debugLog('Unread Count WebSocket disconnected:', event.code, event.reason);
                unreadCountConnectingRef.current = false;
                setUnreadCountConnected(false);
                setUnreadCountSocket(null);

                // Check if connection was stable
                const connectionDuration = unreadCountConnectTimeRef.current
                    ? Date.now() - unreadCountConnectTimeRef.current
                    : 0;

                if (connectionDuration >= minStableConnectionTime) {
                    debugLog('✅ Unread Count connection was stable, resetting reconnect attempts');
                    unreadCountReconnectAttemptsRef.current = 0;
                }
                unreadCountConnectTimeRef.current = null;

                // Don't reconnect if it was an intentional close
                if (unreadCountIntentionalCloseRef.current) {
                    debugLog('⚠️ Intentional close, skipping reconnect');
                    unreadCountIntentionalCloseRef.current = false;
                    setUnreadCountStatus('Disconnected');
                    return;
                }

                // Check if max attempts reached
                if (unreadCountReconnectAttemptsRef.current >= maxReconnectAttempts) {
                    debugLog('❌ Unread Count: Max reconnect attempts reached');
                    setUnreadCountStatus('Connection Failed - Max Retries Reached');
                    return;
                }

                if (event.code === 1000) {
                    setUnreadCountStatus('Disconnected');
                } else {
                    setUnreadCountStatus('Connection Lost');
                    handleUnreadCountReconnect();
                }
            };

            ws.onerror = (error) => {
                console.error('Unread Count WebSocket error:', error);
                unreadCountConnectingRef.current = false;
                setUnreadCountStatus('Connection Error');
            };

            ws.onmessage = (event) => {
                try {
                    debugLog('Unread Count WebSocket message received:', event.data);
                    const data = JSON.parse(event.data);
                    if (data.type === 'unread_count') {
                        setTotalUnreadCount({ total_unread_messages: data.total_unread_messages });
                    }
                } catch (e) {
                    console.error('Error parsing Unread Count WebSocket message:', e);
                }
            };

            return ws;
        } catch (error) {
            unreadCountConnectingRef.current = false;
            setUnreadCountStatus('Connection Failed');
            return null;
        }
    };
    // Notification Unread Count WebSocket Connection
    const connectNotificationUnreadCountWebSocket = () => {
        try {
            if (notificationUnreadCountConnectingRef.current) {
                debugLog('⚠️ Notification Unread Count connection already in progress, skipping');
                return null;
            }

            if (notificationUnreadCountSocket && notificationUnreadCountSocket.readyState !== WebSocket.CLOSED) {
                debugLog('🔌 Closing existing Notification Unread Count socket before creating new one');
                notificationUnreadCountIntentionalCloseRef.current = true;
                notificationUnreadCountSocket.close(1000, 'Reconnecting');
                setNotificationUnreadCountSocket(null);
            }

            const token = getAuthToken();
            if (!token) {
                debugLog('⚠️ No token available, cannot connect Notification Unread Count WebSocket');
                setNotificationUnreadCountStatus('No Token');
                return null;
            }

            notificationUnreadCountConnectingRef.current = true;
            const wsUrl = `${process.env.REACT_APP_SOCKET_URL}/notification/unread-count/?token=${token}`;

            debugLog('Connecting to Notification Unread Count WebSocket:', wsUrl);
            setNotificationUnreadCountStatus('Connecting...');
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                debugLog('Notification Unread Count WebSocket connected successfully');
                notificationUnreadCountConnectingRef.current = false;
                notificationUnreadCountConnectTimeRef.current = Date.now();
                setNotificationUnreadCountConnected(true);
                setNotificationUnreadCountStatus('Connected');
                setNotificationUnreadCountSocket(ws);
            };

            ws.onclose = (event) => {
                debugLog('Notification Unread Count WebSocket disconnected:', event.code, event.reason);
                notificationUnreadCountConnectingRef.current = false;
                setNotificationUnreadCountConnected(false);
                setNotificationUnreadCountSocket(null);

                const connectionDuration = notificationUnreadCountConnectTimeRef.current
                    ? Date.now() - notificationUnreadCountConnectTimeRef.current
                    : 0;

                if (connectionDuration >= minStableConnectionTime) {
                    notificationUnreadCountReconnectAttemptsRef.current = 0;
                }
                notificationUnreadCountConnectTimeRef.current = null;

                if (notificationUnreadCountIntentionalCloseRef.current) {
                    notificationUnreadCountIntentionalCloseRef.current = false;
                    setNotificationUnreadCountStatus('Disconnected');
                    return;
                }

                if (notificationUnreadCountReconnectAttemptsRef.current >= maxReconnectAttempts) {
                    setNotificationUnreadCountStatus('Connection Failed - Max Retries Reached');
                    return;
                }

                if (event.code === 1000) {
                    setNotificationUnreadCountStatus('Disconnected');
                } else {
                    setNotificationUnreadCountStatus('Connection Lost');
                    handleNotificationUnreadCountReconnect();
                }
            };

            ws.onerror = (error) => {
                console.error('Notification Unread Count WebSocket error:', error);
                notificationUnreadCountConnectingRef.current = false;
                setNotificationUnreadCountStatus('Connection Error');
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'notification_unread_count') {
                        setNotificationUnreadCount({ unread_count: data.unread_count });
                    }
                } catch (e) {
                    console.error('Error parsing Notification Unread Count WebSocket message:', e);
                }
            };

            return ws;
        } catch (error) {
            notificationUnreadCountConnectingRef.current = false;
            setNotificationUnreadCountStatus('Connection Failed');
            return null;
        }
    };

    // Unread Request Count WebSocket Connection
    const connectUnreadRequestCountWebSocket = () => {
        try {
            // Prevent duplicate connection attempts
            if (unreadRequestCountConnectingRef.current) {
                debugLog('⚠️ Unread Request Count connection already in progress, skipping');
                return null;
            }

            // Close existing socket if any to prevent duplicates
            if (unreadRequestCountSocket && unreadRequestCountSocket.readyState !== WebSocket.CLOSED) {
                debugLog('🔌 Closing existing Unread Request Count socket before creating new one');
                unreadRequestCountIntentionalCloseRef.current = true;
                unreadRequestCountSocket.close(1000, 'Reconnecting');
                setUnreadRequestCountSocket(null);
            }

            const token = getAuthToken();
            if (!token) {
                debugLog('⚠️ No token available, cannot connect Unread Request Count WebSocket');
                setUnreadRequestCountStatus('No Token');
                return null;
            }

            unreadRequestCountConnectingRef.current = true;
            const wsUrl = `${process.env.REACT_APP_SOCKET_URL}/message-request/unread-count/?token=${token}`;

            debugLog('Connecting to Unread Request Count WebSocket:', wsUrl);
            setUnreadRequestCountStatus('Connecting...');
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                debugLog('Unread Request Count WebSocket connected successfully');
                unreadRequestCountConnectingRef.current = false;
                unreadRequestCountConnectTimeRef.current = Date.now();
                setUnreadRequestCountConnected(true);
                setUnreadRequestCountStatus('Connected');
                setUnreadRequestCountSocket(ws);
            };

            ws.onclose = (event) => {
                debugLog('Unread Request Count WebSocket disconnected:', event.code, event.reason);
                unreadRequestCountConnectingRef.current = false;
                setUnreadRequestCountConnected(false);
                setUnreadRequestCountSocket(null);

                // Check if connection was stable
                const connectionDuration = unreadRequestCountConnectTimeRef.current
                    ? Date.now() - unreadRequestCountConnectTimeRef.current
                    : 0;

                if (connectionDuration >= minStableConnectionTime) {
                    debugLog('✅ Unread Request Count connection was stable, resetting reconnect attempts');
                    unreadRequestCountReconnectAttemptsRef.current = 0;
                }
                unreadRequestCountConnectTimeRef.current = null;

                // Don't reconnect if it was an intentional close
                if (unreadRequestCountIntentionalCloseRef.current) {
                    debugLog('⚠️ Intentional close, skipping reconnect');
                    unreadRequestCountIntentionalCloseRef.current = false;
                    setUnreadRequestCountStatus('Disconnected');
                    return;
                }

                // Check if max attempts reached
                if (unreadRequestCountReconnectAttemptsRef.current >= maxReconnectAttempts) {
                    debugLog('❌ Unread Request Count: Max reconnect attempts reached');
                    setUnreadRequestCountStatus('Connection Failed - Max Retries Reached');
                    return;
                }

                if (event.code === 1000) {
                    setUnreadRequestCountStatus('Disconnected');
                } else {
                    setUnreadRequestCountStatus('Connection Lost');
                    handleUnreadRequestCountReconnect();
                }
            };

            ws.onerror = (error) => {
                console.error('Unread Request Count WebSocket error:', error);
                unreadRequestCountConnectingRef.current = false;
                setUnreadRequestCountStatus('Connection Error');
            };

            ws.onmessage = (event) => {
                try {
                    debugLog('Unread Request Count WebSocket message received:', event.data);
                    const data = JSON.parse(event.data);
                    if (data.type === 'message_request_unread_count') {
                        setTotalUnreadRequestCount({ total_unread_messages: data.total_unread_messages });
                    }
                } catch (e) {
                    console.error('Error parsing Unread Request Count WebSocket message:', e);
                }
            };

            return ws;
        } catch (error) {
            console.error('Failed to connect to Unread Request Count WebSocket:', error);
            unreadRequestCountConnectingRef.current = false;
            setUnreadRequestCountStatus('Connection Failed');
            return null;
        }
    };
    // Livestream List WebSocket Connection
    const connectLivestreamListWebSocket = () => {
        try {
            // Prevent duplicate connection attempts
            if (livestreamListConnectingRef.current) {
                debugLog('⚠️ Livestream List connection already in progress, skipping');
                return null;
            }

            // Close existing socket if any to prevent duplicates
            if (livestreamListSocket && livestreamListSocket.readyState !== WebSocket.CLOSED) {
                debugLog('🔌 Closing existing Livestream List socket before creating new one');
                livestreamListIntentionalCloseRef.current = true;
                livestreamListSocket.close(1000, 'Reconnecting');
                setLivestreamListSocket(null);
            }

            livestreamListConnectingRef.current = true;
            const token = getAuthToken();
            const wsUrl = `${process.env.REACT_APP_SOCKET_URL}/livestream/list/`;

            debugLog('Connecting to Livestream List WebSocket:', wsUrl);
            setLivestreamListStatus('Connecting...');
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                debugLog('Livestream List WebSocket connected successfully');
                livestreamListConnectingRef.current = false;
                livestreamListConnectTimeRef.current = Date.now();
                setLivestreamListConnected(true);
                setLivestreamListStatus('Connected');
                setLivestreamListSocket(ws);
            };

            ws.onclose = (event) => {
                debugLog('Livestream List WebSocket disconnected:', event.code, event.reason);
                livestreamListConnectingRef.current = false;
                setLivestreamListConnected(false);
                setLivestreamListSocket(null);

                // Check if connection was stable
                const connectionDuration = livestreamListConnectTimeRef.current
                    ? Date.now() - livestreamListConnectTimeRef.current
                    : 0;

                if (connectionDuration >= minStableConnectionTime) {
                    debugLog('✅ Livestream List connection was stable, resetting reconnect attempts');
                    livestreamListReconnectAttemptsRef.current = 0;
                }
                livestreamListConnectTimeRef.current = null;

                // Don't reconnect if it was an intentional close
                if (livestreamListIntentionalCloseRef.current) {
                    debugLog('⚠️ Intentional close, skipping reconnect');
                    livestreamListIntentionalCloseRef.current = false;
                    setLivestreamListStatus('Disconnected');
                    return;
                }

                // Check if max attempts reached
                if (livestreamListReconnectAttemptsRef.current >= maxReconnectAttempts) {
                    debugLog('❌ Livestream List: Max reconnect attempts reached');
                    setLivestreamListStatus('Connection Failed - Max Retries Reached');
                    return;
                }

                if (event.code === 1000) {
                    setLivestreamListStatus('Disconnected');
                } else {
                    setLivestreamListStatus('Connection Lost');
                    handleLivestreamListReconnect();
                }
            };

            ws.onerror = (error) => {
                console.error('Livestream List WebSocket error:', error);
                livestreamListConnectingRef.current = false;
                setLivestreamListStatus('Connection Error');
            };

            ws.onmessage = (event) => {
                try {
                    debugLog('📺 Livestream List WebSocket message received:', event.data);
                    const data = JSON.parse(event.data);

                    if (data.type === 'livestream_list' && data.streams) {
                        setLivestreamList(data.streams);
                    }
                } catch (e) {
                    console.error('Error parsing Livestream List WebSocket message:', e);
                }
            };

            return ws;
        } catch (error) {
            console.error('Failed to connect to Livestream List WebSocket:', error);
            livestreamListConnectingRef.current = false;
            setLivestreamListStatus('Connection Failed');
            return null;
        }
    };


    const handleCommunityChatReconnect = () => {
        // Don't reconnect if already attempting
        if (communityChatConnectingRef.current) {
            debugLog('⚠️ Reconnect skipped - connection already in progress');
            return;
        }

        // Clear any existing timeout
        if (communityChatReconnectTimeoutRef.current) {
            clearTimeout(communityChatReconnectTimeoutRef.current);
            communityChatReconnectTimeoutRef.current = null;
        }

        if (communityChatReconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, communityChatReconnectAttemptsRef.current), 30000);
            debugLog(`🔄 Scheduling reconnect in ${delay}ms (attempt ${communityChatReconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
            setCommunityChatStatus(`Reconnecting... (${communityChatReconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
            communityChatReconnectTimeoutRef.current = setTimeout(() => {
                communityChatReconnectAttemptsRef.current++;
                connectCommunityChatWebSocket();
            }, delay);
        } else {
            debugLog('❌ Max reconnect attempts reached');
            setCommunityChatStatus('Connection Failed');
        }
    };

    const handleChatRoomListReconnect = () => {
        // Don't reconnect if already attempting
        if (chatRoomListConnectingRef.current) {
            debugLog('⚠️ Chat Room List reconnect skipped - connection already in progress');
            return;
        }

        // Clear any existing timeout
        if (chatRoomListReconnectTimeoutRef.current) {
            clearTimeout(chatRoomListReconnectTimeoutRef.current);
            chatRoomListReconnectTimeoutRef.current = null;
        }

        if (chatRoomListReconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, chatRoomListReconnectAttemptsRef.current), 30000);
            setChatRoomListStatus(`Reconnecting... (${chatRoomListReconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
            chatRoomListReconnectTimeoutRef.current = setTimeout(() => {
                chatRoomListReconnectAttemptsRef.current++;
                connectChatRoomListWebSocket();
            }, delay);
        } else {
            setChatRoomListStatus('Connection Failed');
        }
    };

    const handleMessageRequestListReconnect = () => {
        // Don't reconnect if already attempting
        if (messageRequestListConnectingRef.current) {
            debugLog('⚠️ Message Request List reconnect skipped - connection already in progress');
            return;
        }

        // Clear any existing timeout
        if (messageRequestListReconnectTimeoutRef.current) {
            clearTimeout(messageRequestListReconnectTimeoutRef.current);
            messageRequestListReconnectTimeoutRef.current = null;
        }

        if (messageRequestListReconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, messageRequestListReconnectAttemptsRef.current), 30000);
            setMessageRequestListStatus(`Reconnecting... (${messageRequestListReconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
            messageRequestListReconnectTimeoutRef.current = setTimeout(() => {
                messageRequestListReconnectAttemptsRef.current++;
                connectMessageRequestListWebSocket();
            }, delay);
        } else {
            setMessageRequestListStatus('Connection Failed');
        }
    };

    const handleUnreadCountReconnect = () => {
        // Don't reconnect if already attempting
        if (unreadCountConnectingRef.current) {
            debugLog('⚠️ Unread Count reconnect skipped - connection already in progress');
            return;
        }

        // Clear any existing timeout
        if (unreadCountReconnectTimeoutRef.current) {
            clearTimeout(unreadCountReconnectTimeoutRef.current);
            unreadCountReconnectTimeoutRef.current = null;
        }

        if (unreadCountReconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, unreadCountReconnectAttemptsRef.current), 30000);
            setUnreadCountStatus(`Reconnecting... (${unreadCountReconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
            unreadCountReconnectTimeoutRef.current = setTimeout(() => {
                unreadCountReconnectAttemptsRef.current++;
                connectUnreadCountWebSocket();
            }, delay);
        } else {
            setUnreadCountStatus('Connection Failed');
        }
    };

    const handleUnreadRequestCountReconnect = () => {
        // Don't reconnect if already attempting
        if (unreadRequestCountConnectingRef.current) {
            debugLog('⚠️ Unread Request Count reconnect skipped - connection already in progress');
            return;
        }

        // Clear any existing timeout
        if (unreadRequestCountReconnectTimeoutRef.current) {
            clearTimeout(unreadRequestCountReconnectTimeoutRef.current);
            unreadRequestCountReconnectTimeoutRef.current = null;
        }

        if (unreadRequestCountReconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, unreadRequestCountReconnectAttemptsRef.current), 30000);
            setUnreadRequestCountStatus(`Reconnecting... (${unreadRequestCountReconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
            unreadRequestCountReconnectTimeoutRef.current = setTimeout(() => {
                unreadRequestCountReconnectAttemptsRef.current++;
                connectUnreadRequestCountWebSocket();
            }, delay);
        } else {
            setUnreadRequestCountStatus('Connection Failed');
        }
    };

    const handleNotificationUnreadCountReconnect = () => {
        if (notificationUnreadCountConnectingRef.current) {
            return;
        }

        if (notificationUnreadCountReconnectTimeoutRef.current) {
            clearTimeout(notificationUnreadCountReconnectTimeoutRef.current);
            notificationUnreadCountReconnectTimeoutRef.current = null;
        }

        if (notificationUnreadCountReconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, notificationUnreadCountReconnectAttemptsRef.current), 30000);
            setNotificationUnreadCountStatus(`Reconnecting... (${notificationUnreadCountReconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
            notificationUnreadCountReconnectTimeoutRef.current = setTimeout(() => {
                notificationUnreadCountReconnectAttemptsRef.current++;
                connectNotificationUnreadCountWebSocket();
            }, delay);
        } else {
            setNotificationUnreadCountStatus('Connection Failed');
        }
    };

    const handleLivestreamListReconnect = () => {
        // Don't reconnect if already attempting
        if (livestreamListConnectingRef.current) {
            debugLog('⚠️ Livestream List reconnect skipped - connection already in progress');
            return;
        }

        // Clear any existing timeout
        if (livestreamListReconnectTimeoutRef.current) {
            clearTimeout(livestreamListReconnectTimeoutRef.current);
            livestreamListReconnectTimeoutRef.current = null;
        }

        if (livestreamListReconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, livestreamListReconnectAttemptsRef.current), 30000);
            setLivestreamListStatus(`Reconnecting... (${livestreamListReconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
            livestreamListReconnectTimeoutRef.current = setTimeout(() => {
                livestreamListReconnectAttemptsRef.current++;
                connectLivestreamListWebSocket();
            }, delay);
        } else {
            setLivestreamListStatus('Connection Failed');
        }
    };

    const fetchMoreChatRooms = () => {
        const socket = chatRoomListSocketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) return;
        if (!chatRoomListNextCursor) return;
        if (chatRoomListIsFetchMoreRef.current) return;

        chatRoomListIsFetchMoreRef.current = true;
        setChatRoomListIsFetchingMore(true);
        socket.send(JSON.stringify({
            action: 'fetch_chat_room_detail_list',
            cursor: chatRoomListNextCursor
        }));
    };

    const sendCommunityChatMessage = (message) => {
        // Validate message input to prevent empty/null message sends
        if (!message || typeof message !== 'object') {
            debugLog('❌ sendCommunityChatMessage called with invalid message:', message);
            return false;
        }

        // Check if message content exists and is not empty
        if (!message.message || typeof message.message !== 'string' || !message.message.trim()) {
            debugLog('❌ sendCommunityChatMessage called with empty message content:', message);
            return false;
        }

        // Check WebSocket connection
        if (!communityChatSocket || communityChatSocket.readyState !== WebSocket.OPEN) {
            console.warn('⚠️ Community Chat WebSocket is not connected. State:', communityChatSocket?.readyState);
            return false;
        }

        try {
            const messageToSend = {
                message: message.message.trim(),
            };

            communityChatSocket.send(JSON.stringify(messageToSend));
            debugLog('📤 Message sent via WebSocket:', message.message);
            return true;
        } catch (error) {
            console.error('❌ Failed to send Community Chat message:', error);
            return false;
        }
    };

    const clearCommunityChatMessages = () => {
        setCommunityChatMessages([]);
        setCommunityChatHasMore(false);
        setCommunityChatNextCursor(null);
        setCommunityChatNextBucket(null);
    };

    const disconnectCommunityChat = () => {
        // Clear any pending reconnect timeout
        if (communityChatReconnectTimeoutRef.current) {
            clearTimeout(communityChatReconnectTimeoutRef.current);
            communityChatReconnectTimeoutRef.current = null;
        }

        // Reset connecting flag and reconnect attempts
        communityChatConnectingRef.current = false;
        communityChatReconnectAttemptsRef.current = 0;

        if (communityChatSocket) {
            communityChatIntentionalCloseRef.current = true;
            communityChatSocket.close(1000, 'User disconnected');
        }
        setCommunityChatSocket(null);
        setCommunityChatConnected(false);
        setCommunityChatStatus('Disconnected');
        setCommunityChatMessages([]);
    };


    // Initialize connections
    useEffect(() => {
        const token = getAuthToken();

        // Only initialize if we have a token
        if (!token) {
            debugLog('⚠️ No token found, skipping WebSocket initialization');
            return;
        }

        debugLog('🔌 Initializing WebSocket connections...');
        // Initialize Community Chat WebSocket connection
        connectCommunityChatWebSocket();
        // Initialize Chat Room List WebSocket connection
        connectChatRoomListWebSocket();
        // Initialize Message Request List WebSocket connection
        connectMessageRequestListWebSocket();
        // Initialize Unread Count WebSocket connection
        connectUnreadCountWebSocket();
        // Initialize Unread Request Count WebSocket connection
        connectUnreadRequestCountWebSocket();
        // Initialize Notification Unread Count WebSocket connection
        connectNotificationUnreadCountWebSocket();
        // Initialize Livestream List WebSocket connection
        // connectLivestreamListWebSocket();

        // Cleanup on component unmount
        return () => {
            debugLog('🧹 Cleaning up all WebSocket connections on unmount...');
            disconnectCommunityChat();

            if (chatRoomListReconnectTimeoutRef.current) {
                clearTimeout(chatRoomListReconnectTimeoutRef.current);
            }
            if (chatRoomListSocket) {
                chatRoomListIntentionalCloseRef.current = true;
                chatRoomListSocket.close(1000, 'User disconnected');
            }
            setChatRoomListSocket(null);
            setChatRoomListConnected(false);
            setChatRoomListStatus('Disconnected');

            if (messageRequestListReconnectTimeoutRef.current) {
                clearTimeout(messageRequestListReconnectTimeoutRef.current);
            }
            if (messageRequestListSocket) {
                messageRequestListIntentionalCloseRef.current = true;
                messageRequestListSocket.close(1000, 'User disconnected');
            }
            setMessageRequestListSocket(null);
            setMessageRequestListConnected(false);
            setMessageRequestListStatus('Disconnected');

            if (unreadCountReconnectTimeoutRef.current) {
                clearTimeout(unreadCountReconnectTimeoutRef.current);
            }
            if (unreadCountSocket) {
                unreadCountIntentionalCloseRef.current = true;
                unreadCountSocket.close(1000, 'User disconnected');
            }
            setUnreadCountSocket(null);
            setUnreadCountConnected(false);
            setUnreadCountStatus('Disconnected');

            if (unreadRequestCountReconnectTimeoutRef.current) {
                clearTimeout(unreadRequestCountReconnectTimeoutRef.current);
            }
            if (unreadRequestCountSocket) {
                unreadRequestCountIntentionalCloseRef.current = true;
                unreadRequestCountSocket.close(1000, 'User disconnected');
            }
            setUnreadRequestCountSocket(null);
            setUnreadRequestCountConnected(false);
            setUnreadRequestCountStatus('Disconnected');

            if (livestreamListReconnectTimeoutRef.current) {
                clearTimeout(livestreamListReconnectTimeoutRef.current);
            }
            if (livestreamListSocket) {
                livestreamListIntentionalCloseRef.current = true;
                livestreamListSocket.close(1000, 'User disconnected');
            }
            setLivestreamListSocket(null);
            setLivestreamListConnected(false);
            setLivestreamListStatus('Disconnected');

            if (notificationUnreadCountReconnectTimeoutRef.current) {
                clearTimeout(notificationUnreadCountReconnectTimeoutRef.current);
            }
            if (notificationUnreadCountSocket) {
                notificationUnreadCountIntentionalCloseRef.current = true;
                notificationUnreadCountSocket.close(1000, 'User disconnected');
            }
            setNotificationUnreadCountSocket(null);
            setNotificationUnreadCountConnected(false);
            setNotificationUnreadCountStatus('Disconnected');
        };
        // Empty dependency array - only run once on mount
    }, []);


    // Context value with multiple WebSocket connections
    const contextValue = {
        // Community Chat WebSocket
        communityChat: {
            socket: communityChatSocket,
            isConnected: communityChatConnected,
            connectionStatus: communityChatStatus,
            messages: communityChatMessages,
            hasMore: communityChatHasMore,
            nextCursor: communityChatNextCursor,
            nextBucket: communityChatNextBucket,
            sendMessage: (message) => {
                // Add extra validation layer in context value
                if (!message || !message.message || !message.message.trim()) {
                    debugLog('❌ Context sendMessage called with invalid data:', message);
                    return false;
                }
                return sendCommunityChatMessage(message);
            },
            clearMessages: clearCommunityChatMessages,
            disconnect: disconnectCommunityChat,
            reconnect: connectCommunityChatWebSocket
        },

        // Chat Room List WebSocket
        chatRoomList: {
            socket: chatRoomListSocket,
            isConnected: chatRoomListConnected,
            connectionStatus: chatRoomListStatus,
            data: chatRoomList,
            reconnect: connectChatRoomListWebSocket,
            fetchMore: fetchMoreChatRooms,
            hasMore: chatRoomListHasMore,
            isFetchingMore: chatRoomListIsFetchingMore,
            nextCursor: chatRoomListNextCursor,
        },

        // Message Request List WebSocket
        messageRequestList: {
            socket: messageRequestListSocket,
            isConnected: messageRequestListConnected,
            connectionStatus: messageRequestListStatus,
            data: messageRequestList,
            reconnect: connectMessageRequestListWebSocket
        },

        // Unread Count WebSocket
        unreadCount: {
            socket: unreadCountSocket,
            isConnected: unreadCountConnected,
            connectionStatus: unreadCountStatus,
            data: totalUnreadCount,
            reconnect: connectUnreadCountWebSocket
        },

        // Unread Request Count WebSocket
        unreadRequestCount: {
            socket: unreadRequestCountSocket,
            isConnected: unreadRequestCountConnected,
            connectionStatus: unreadRequestCountStatus,
            data: totalUnreadRequestCount,
            reconnect: connectUnreadRequestCountWebSocket
        },

        // Livestream List WebSocket
        livestreamList: {
            socket: livestreamListSocket,
            isConnected: livestreamListConnected,
            connectionStatus: livestreamListStatus,
            data: livestreamList,
            reconnect: connectLivestreamListWebSocket
        },

        // Notification Unread Count WebSocket
        notificationUnreadCount: {
            socket: notificationUnreadCountSocket,
            isConnected: notificationUnreadCountConnected,
            connectionStatus: notificationUnreadCountStatus,
            data: notificationUnreadCount,
            reconnect: connectNotificationUnreadCountWebSocket
        },

        // Account switching utilities
        currentToken,
        refreshConnections: () => {
            debugLog('🔄 Manual connection refresh requested...');
            disconnectAllWebSockets();
            reconnectAllWebSockets();
        },
        disconnectAll: disconnectAllWebSockets,

        // Legacy support (maps to community chat for backward compatibility)
        socket: communityChatSocket,
        isConnected: communityChatConnected,
        connectionStatus: communityChatStatus,
        sendMessage: (message) => {
            // Add extra validation layer for legacy usage
            if (!message || !message.message || !message.message.trim()) {
                debugLog('❌ Legacy sendMessage called with invalid data:', message);
                return false;
            }
            return sendCommunityChatMessage(message);
        },
        disconnect: disconnectCommunityChat,
        reconnect: connectCommunityChatWebSocket
    };

    return (
        <SocketContext.Provider value={contextValue}>
            {children}
        </SocketContext.Provider>
    );
};
